import type {
  BaseRecord,
  CrudFilters,
  DataProvider,
  GetListParams,
  GetOneParams,
} from '@refinedev/core'

import { ordersGetReturn, ordersListReceivedReturns } from '@repo/sdk'

import { i18n } from '../shared/i18n'

import { handleSdkError } from './sdkErrorUtils'
import { getApiUrl } from '../lib/runtime-env'

function extractStatus(
  filters?: CrudFilters,
): 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'REFUNDED' | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if ('field' in filter && filter.field === 'status' && 'value' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- CrudFilter.value is typed as any in Refine
      return filter.value as 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'REFUNDED'
    }
  }
  return undefined
}

function notImplemented(): never {
  throw new Error(i18n.t('common.errors.notImplemented'))
}

export const returnListDataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>(params: GetListParams) => {
    try {
      const page = params.pagination?.currentPage ?? 1
      const pageSize = params.pagination?.pageSize ?? 20
      const status = extractStatus(params.filters)

      const response = await ordersListReceivedReturns({
        query: { page, pageSize, status },
      })

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Refine DataProvider generic requires TData cast
        data: (response.data?.data ?? []) as unknown as TData[],
        total: response.data?.meta.totalItems ?? 0,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(params: GetOneParams) => {
    try {
      const returnId = String(params.id)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- meta values are typed as any in Refine
      const orderId = params.meta?.orderId as string

      if (!orderId) {
        throw new Error(i18n.t('common.errors.orderIdRequired'))
      }

      const response = await ordersGetReturn({
        path: { orderId, returnId },
      })

      if (!response.data) {
        throw new Error(i18n.t('common.errors.detailNotFound'))
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Refine DataProvider generic requires TData cast
        data: response.data as unknown as TData,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },
  create: notImplemented,
  update: notImplemented,
  deleteOne: notImplemented,
  getApiUrl,
}
